import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxPhotoEditorService } from 'ngx-photo-editor';
import { DocumentUtilsService } from 'src/app/services/document-utils.service';
import { FormCompanyService } from 'src/app/layouts/main-v2/services/forms/form-company.service';
import { CnpjInfoService } from 'src/app/services/cnpj-info.service';
import { PostalCodeService } from 'src/app/services/postal-code.service';
import { AuthService } from 'src/app/services/auth.service';
import { ClinicGroupService } from 'src/app/services/clinic-group.service';
import { IClinicGroup } from 'src/app/models/clinic-group.model';
import { DropdownOption } from 'src/app/layouts/main-v2/components/custom-dropdown/custom-dropdown.component';
import { ClinicAppService } from 'src/app/services/clinic-app.service';
import { IClinicApp, IClinicAppCreated, AVAILABLE_SCOPES } from 'src/app/models/clinic-app.model';
import { Store } from '@ngrx/store';
import { IError } from 'src/app/models/error.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';

@Component({
  selector: 'app-company-info-v2',
  templateUrl: './info.component.html',
  styleUrls: ['./info.component.sass']
})
export class InfoComponent implements OnInit, OnDestroy {
  formCompanyInfo!: FormGroup;

  // Imagem da logo: URL do S3 (quando carregada da API) ou object URL local (quando selecionada)
  imageBinary?: string;

  // Arquivo selecionado localmente (pendente de upload para o S3)
  pendingLogoFile?: File;

  readonly logoPositions = [
    { label: 'Cabecalho', value: 'header' },
    { label: 'Marca dagua', value: 'watermark' }
  ];

  // Loading state for CNPJ auto-fill
  isLoadingCnpj = false;
  
  // Loading state for CEP auto-fill
  isLoadingCep = false;

  // Modo de edição
  isEditMode = false;

  // Controle do modal de visualização da imagem
  showImagePreview = false;

  // Flag para verificar se o usuário é ROOT
  isRoot = false;

  // Flag para verificar se o usuário é ADMIN
  isAdmin = false;

  // Grupos de clínicas para o dropdown
  clinicGroupOptions: DropdownOption[] = [];
  isLoadingGroups = false;

  // === Clinic Apps (API para Terceiros) ===
  clinicApps: IClinicApp[] = [];
  clinicAppsLoading = false;
  showCreateAppModal = false;
  showSecretModal = false;
  showEditScopesModal = false;
  showDeleteAppModal = false;
  deleteAppStep: 'confirm' | 'loading' = 'confirm';
  showRegenerateConfirmModal = false;
  regenerateAppStep: 'confirm' | 'loading' = 'confirm';
  regeneratingApp: IClinicApp | null = null;
  newAppName = '';
  newAppScopes: string[] = [];
  availableScopes = AVAILABLE_SCOPES;
  createdAppSecret: IClinicAppCreated | null = null;
  credentialsCopied = false;
  editingApp: IClinicApp | null = null;
  editingScopes: string[] = [];
  deletingApp: IClinicApp | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly companyFormService: FormCompanyService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly documentUtils: DocumentUtilsService,
    private readonly photoService: NgxPhotoEditorService,
    private readonly cnpjInfoService: CnpjInfoService,
    private readonly postalCodeService: PostalCodeService,
    private readonly authService: AuthService,
    private readonly clinicGroupService: ClinicGroupService,
    private readonly clinicAppService: ClinicAppService,
    private readonly store: Store<{ warningPopup: IError }>,
  ) {
    const permission = this.authService.getPermission();
    this.isRoot = permission === 'ROOT';
    this.isAdmin = permission === 'ADMIN';
  }

  ngOnInit(): void {
    this.isEditMode = this.companyFormService.isEditMode();
    this.buildForm();
    this.loadClinicGroups();

    // Carrega clinic apps se ADMIN e em modo edição
    if (this.isAdmin && this.isEditMode) {
      this.loadClinicApps();
    }
    
    // Se for modo de edição, escuta quando os dados são carregados
    if (this.isEditMode) {
      const savedInfo = this.companyFormService.getInfo();
      if (savedInfo) {
        this.populateForm();
      }
      // Escuta atualizações dos dados (ex: após salvar com nova logo S3)
      this.companyFormService.isDataLoaded().subscribe(loaded => {
        if (loaded) {
          this.populateForm();
        }
      });
    }
  }

  private buildForm(): void {
    const savedInfo = this.companyFormService.getInfo();
    const savedAddress = this.companyFormService.getAddress();

    this.formCompanyInfo = this.fb.group({
      // Info fields
      image: [savedInfo?.image ?? ''],
      imagePosition: [savedInfo?.imagePosition ?? 'header', Validators.required],
      cnpj: [
        savedInfo?.cnpj ?? '',
        [Validators.required, this.documentUtils.cpfCnpjValidator()]
      ],
      corporateName: [savedInfo?.corporateName ?? '', Validators.required],
      // Address fields
      cep: [savedAddress?.cep ?? '', Validators.required],
      street: [savedAddress?.street ?? '', Validators.required],
      number: [savedAddress?.number ?? '', Validators.required],
      complement: [savedAddress?.complement ?? ''],
      neighborhood: [savedAddress?.neighborhood ?? '', Validators.required],
      city: [savedAddress?.city ?? '', Validators.required],
      state: [savedAddress?.state ?? '', Validators.required],
      // Campos de Testemunha
  firstWitnessName: [savedInfo?.firstWitnessName ?? 'Yasmin de Miranda Martins Fernandes'],
  firstWitnessDocument: [savedInfo?.firstWitnessDocument ?? '057.814.281-35'],
  secondWitnessName: [savedInfo?.secondWitnessName ?? 'Luciana Gabriela Cirino'],
  secondWitnessDocument: [savedInfo?.secondWitnessDocument ?? '445.958.308-90'],
      // Grupo pertencente (apenas criação)
      groupId: [savedInfo?.groupId ?? ''],
      // Email jurídico (somente leitura no modo edição)
      legalEmail: [{ value: '', disabled: true }]
    });

    // Se estiver em modo de edição, o CNPJ não pode ser alterado
    if (this.isEditMode) {
      this.formCompanyInfo.get('cnpj')?.disable();
    }

    // Desabilita campos para usuários não-ROOT (somente endereço pode ser editado)
    if (!this.isRoot) {
      this.formCompanyInfo.get('cnpj')?.disable();
      this.formCompanyInfo.get('corporateName')?.disable();
      this.formCompanyInfo.get('imagePosition')?.disable();
      this.formCompanyInfo.get('firstWitnessName')?.disable();
      this.formCompanyInfo.get('firstWitnessDocument')?.disable();
      this.formCompanyInfo.get('secondWitnessName')?.disable();
      this.formCompanyInfo.get('secondWitnessDocument')?.disable();
    }

    // Inicializa imageBinary se já tiver imagem salva (URL do S3 ou base64 legado)
    if (savedInfo?.image && typeof savedInfo.image === 'string') {
      this.imageBinary = savedInfo.image;
    }
  }

  private populateForm(): void {
    const savedInfo = this.companyFormService.getInfo();
    const savedAddress = this.companyFormService.getAddress();
    
    if (savedInfo) {
      this.formCompanyInfo.patchValue({
        image: savedInfo.image,
        imagePosition: savedInfo.imagePosition ?? 'header',
        cnpj: savedInfo.cnpj,
        corporateName: savedInfo.corporateName,
        // Campos de Testemunha
        firstWitnessName: savedInfo.firstWitnessName ?? '',
        firstWitnessDocument: savedInfo.firstWitnessDocument ?? '',
        secondWitnessName: savedInfo.secondWitnessName ?? '',
        secondWitnessDocument: savedInfo.secondWitnessDocument ?? '',
      });

      // Popula email jurídico a partir dos dados de contato
      const savedContact = this.companyFormService.getContact();
      if (savedContact?.legalEmail) {
        this.formCompanyInfo.patchValue({ legalEmail: savedContact.legalEmail });
      }

      if (savedInfo.image && typeof savedInfo.image === 'string') {
        this.imageBinary = savedInfo.image;
        this.pendingLogoFile = undefined;
      }
    }
    
    if (savedAddress) {
      this.formCompanyInfo.patchValue({
        cep: savedAddress.cep ?? '',
        street: savedAddress.street ?? '',
        number: savedAddress.number ?? '',
        complement: savedAddress.complement ?? '',
        neighborhood: savedAddress.neighborhood ?? '',
        city: savedAddress.city ?? '',
        state: savedAddress.state ?? ''
      });
    }
  }

  /**
   * Abre o photo editor quando o usuário seleciona um arquivo
   */
  onFileSelect(event: Event): void {
    this.photoService.open(event, {}).subscribe(data => {
      if (data?.file) {
        this.setLogoFile(data.file);
      }
    });
  }

  /**
   * Armazena o arquivo de logo e cria URL de preview local
   */
  private setLogoFile(file: File): void {
    this.pendingLogoFile = file;
    this.imageBinary = URL.createObjectURL(file);
    this.formCompanyInfo.get('image')?.setValue(file);
  }

  /**
   * Remove a imagem selecionada
   */
  resetImage(): void {
    this.pendingLogoFile = undefined;
    this.imageBinary = undefined;
    this.formCompanyInfo.get('image')?.setValue('');
    // Reset any file input to allow re-selecting the same file
    const fileInputs = document.querySelectorAll('input[type="file"]');
    fileInputs.forEach((input: Element) => {
      (input as HTMLInputElement).value = '';
    });
  }

  /**
   * Retorna a URL da logo para exibição.
   * Pode ser: URL do S3 (carregada da API), object URL local (novo arquivo),
   * ou base64 legado (prefixado com data:image).
   */
  getLogoUrl(): string | null {
    if (!this.imageBinary) return null;
    // URL do S3 ou blob URL local
    if (this.imageBinary.startsWith('http') || this.imageBinary.startsWith('blob:')) {
      return this.imageBinary;
    }
    // Base64 legado (antes da migração para S3)
    return 'data:image/png;base64,' + this.imageBinary;
  }

  onCpfCnpjInput(event: Event): void {
    const control = this.formCompanyInfo.get('cnpj');
    if (control) {
      this.documentUtils.onCpfCnpjInput(event, control);
      
      // Check if CNPJ is complete (14 digits) and trigger auto-fill
      const digits = control.value?.replace(/\D/g, '') || '';
      if (digits.length === 14) {
        this.fetchCnpjInfo(digits);
      }
    }
  }

  /**
   * Fetches company info from CNPJ API and auto-fills form fields
   */
  private fetchCnpjInfo(cnpj: string): void {
    this.isLoadingCnpj = true;
    this.cnpjInfoService.getCnpjInfo(cnpj)
      .then((response) => {
        if (response.status) {
          // Error response, skip auto-fill
          return;
        }
        // Auto-fill corporate name
        if (response.razao_social) {
          this.formCompanyInfo.get('corporateName')?.setValue(response.razao_social);
        }
        // Auto-fill address fields directly in this form
        if (response.estabelecimento) {
          this.formCompanyInfo.patchValue({
            cep: this.formatCep(response.estabelecimento.cep || ''),
            street: response.estabelecimento.logradouro || '',
            number: response.estabelecimento.numero || '',
            complement: response.estabelecimento.complemento || '',
            neighborhood: response.estabelecimento.bairro || '',
            city: response.estabelecimento.cidade?.nome || '',
            state: response.estabelecimento.estado?.sigla || ''
          });
        }
      })
      .finally(() => {
        this.isLoadingCnpj = false;
      });
  }

  /**
   * Formats CEP with mask
   */
  private formatCep(cep: string): string {
    const digits = cep.replace(/\D/g, '');
    if (digits.length === 8) {
      return digits.replace(/^(\d{5})(\d{3})$/, '$1-$2');
    }
    return cep;
  }

  /**
   * Handle CEP input with mask and auto-fill
   */
  async onCepInput(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');

    // Aplica máscara de CEP: 00000-000
    if (value.length > 5) {
      value = value.substring(0, 5) + '-' + value.substring(5, 8);
    }
    input.value = value;
    this.formCompanyInfo.get('cep')?.setValue(value, { emitEvent: false });

    // Busca endereço quando CEP tiver 8 dígitos
    const digits = value.replace(/\D/g, '');
    if (digits.length === 8) {
      await this.searchCep(digits);
    }
  }

  private async searchCep(cep: string): Promise<void> {
    this.isLoadingCep = true;
    try {
      const address = await this.postalCodeService.fetchAddressForBrazilianCEP(cep);
      if (address && !address.erro) {
        this.formCompanyInfo.patchValue({
          street: address.logradouro,
          neighborhood: address.bairro,
          city: address.localidade,
          state: address.uf
        });
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      this.isLoadingCep = false;
    }
  }

  cancel(): void {
    this.companyFormService.clear();
    this.router.navigate(['/main/v2/company']);
  }

  saveAndNext(): void {
    if (this.formCompanyInfo.invalid) {
      this.formCompanyInfo.markAllAsTouched();
      return;
    }
    
    // Salva os dados no serviço
    this.saveFormDataToService();
    
    // Navega para o próximo step
    const allowed = this.companyFormService.getAllowedRoutes();
    if (allowed.includes('contact')) {
      this.router.navigate(['../contact'], { relativeTo: this.route });
    } else if (allowed.length > 0) {
      const last = allowed[allowed.length - 1];
      this.router.navigate(['..', last], { relativeTo: this.route });
    }
  }

  ngOnDestroy(): void {
    // Salva os dados automaticamente quando o componente for destruído
    // (por exemplo, ao navegar para outra aba via stepper)
    this.saveFormDataToService();
  }

  private saveFormDataToService(): void {
    // Preserva os dados existentes do serviço (importante para modo edit)
    const existingInfo = this.companyFormService.getInfo();
    
    // IMPORTANTE: usar getRawValue() em vez de .value para incluir campos disabled
    // (ex.: CNPJ e corporateName ficam disabled em modo edição e para não-ROOT)
    const formValues = this.formCompanyInfo.getRawValue();

    // Save info data
    this.companyFormService.setInfo({
      ...existingInfo,
      image: formValues.image,
      imagePosition: formValues.imagePosition,
      cnpj: formValues.cnpj,
      corporateName: formValues.corporateName,
      // Campos de Testemunha
      firstWitnessName: formValues.firstWitnessName,
      firstWitnessDocument: formValues.firstWitnessDocument,
      secondWitnessName: formValues.secondWitnessName,
      secondWitnessDocument: formValues.secondWitnessDocument,
      groupId: formValues.groupId,
    });
    
    // Save address data
    this.companyFormService.setAddress({
      cep: formValues.cep,
      street: formValues.street,
      number: formValues.number,
      complement: formValues.complement,
      neighborhood: formValues.neighborhood,
      city: formValues.city,
      state: formValues.state
    });
  }

  // Métodos para o modal de visualização da imagem
  openImagePreview(): void {
    if (this.getLogoUrl()) {
      this.showImagePreview = true;
    }
  }

  closeImagePreview(): void {
    this.showImagePreview = false;
  }

  // === CLINIC APPS (API para Terceiros) ===
  loadClinicApps(): void {
    this.clinicAppsLoading = true;
    this.clinicAppService.listApps().subscribe({
      next: (apps) => {
        this.clinicApps = apps.filter(a => a.is_active);
        this.clinicAppsLoading = false;
      },
      error: () => {
        this.clinicAppsLoading = false;
      }
    });
  }

  openCreateAppModal(): void {
    this.newAppName = '';
    this.newAppScopes = [];
    this.showCreateAppModal = true;
  }

  closeCreateAppModal(): void {
    this.showCreateAppModal = false;
  }

  toggleNewAppScope(scope: string): void {
    const idx = this.newAppScopes.indexOf(scope);
    if (idx > -1) {
      this.newAppScopes.splice(idx, 1);
    } else {
      this.newAppScopes.push(scope);
    }
  }

  isNewAppScopeSelected(scope: string): boolean {
    return this.newAppScopes.includes(scope);
  }

  createApp(): void {
    if (!this.newAppName.trim()) {
      this.store.dispatch(setWarningText('Informe um nome para o app.'));
      return;
    }
    if (this.newAppScopes.length === 0) {
      this.store.dispatch(setWarningText('Selecione pelo menos um escopo.'));
      return;
    }

    this.clinicAppService.createApp({
      name: this.newAppName.trim(),
      scopes: this.newAppScopes
    }).subscribe({
      next: (created) => {
        this.createdAppSecret = created;
        this.credentialsCopied = false;
        this.showCreateAppModal = false;
        this.showSecretModal = true;
        this.loadClinicApps();
      },
      error: (err) => {
        const msg = err?.error?.message || 'Erro ao criar app.';
        this.store.dispatch(setWarningText(msg));
      }
    });
  }

  closeSecretModal(): void {
    this.showSecretModal = false;
    this.createdAppSecret = null;
    this.credentialsCopied = false;
  }

  copyCredentials(): void {
    if (!this.createdAppSecret) return;
    const json = JSON.stringify({
      app_key: this.createdAppSecret.app_key,
      app_secret: this.createdAppSecret.app_secret,
      grant_type: 'client_credentials'
    }, null, 2);
    navigator.clipboard.writeText(json).then(() => {
      this.credentialsCopied = true;
      setTimeout(() => this.credentialsCopied = false, 2000);
    });
  }

  regenerateSecret(app: IClinicApp): void {
    this.regeneratingApp = app;
    this.regenerateAppStep = 'confirm';
    this.showRegenerateConfirmModal = true;
  }

  closeRegenerateConfirmModal(): void {
    this.showRegenerateConfirmModal = false;
    this.regeneratingApp = null;
    this.regenerateAppStep = 'confirm';
  }

  onRegenerateModalBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && this.regenerateAppStep !== 'loading') {
      this.closeRegenerateConfirmModal();
    }
  }

  confirmRegenerateSecret(): void {
    if (!this.regeneratingApp) return;
    this.regenerateAppStep = 'loading';

    this.clinicAppService.regenerateSecret(this.regeneratingApp.id).subscribe({
      next: (result) => {
        this.createdAppSecret = result;
        this.credentialsCopied = false;
        this.showRegenerateConfirmModal = false;
        this.regeneratingApp = null;
        this.regenerateAppStep = 'confirm';
        this.showSecretModal = true;
      },
      error: (err) => {
        this.regenerateAppStep = 'confirm';
        const msg = err?.error?.message || 'Erro ao regenerar secret.';
        this.store.dispatch(setWarningText(msg));
      }
    });
  }

  openEditScopesModal(app: IClinicApp): void {
    this.editingApp = app;
    this.editingScopes = [...app.scopes];
    this.showEditScopesModal = true;
  }

  closeEditScopesModal(): void {
    this.showEditScopesModal = false;
    this.editingApp = null;
  }

  toggleEditScope(scope: string): void {
    const idx = this.editingScopes.indexOf(scope);
    if (idx > -1) {
      this.editingScopes.splice(idx, 1);
    } else {
      this.editingScopes.push(scope);
    }
  }

  isEditScopeSelected(scope: string): boolean {
    return this.editingScopes.includes(scope);
  }

  saveScopes(): void {
    if (!this.editingApp) return;
    if (this.editingScopes.length === 0) {
      this.store.dispatch(setWarningText('Selecione pelo menos um escopo.'));
      return;
    }

    this.clinicAppService.updateScopes(this.editingApp.id, { scopes: this.editingScopes }).subscribe({
      next: () => {
        this.store.dispatch(setWarningText('Escopos atualizados com sucesso!'));
        this.closeEditScopesModal();
        this.loadClinicApps();
      },
      error: (err) => {
        const msg = err?.error?.message || 'Erro ao atualizar escopos.';
        this.store.dispatch(setWarningText(msg));
      }
    });
  }

  openDeleteAppModal(app: IClinicApp): void {
    this.deletingApp = app;
    this.deleteAppStep = 'confirm';
    this.showDeleteAppModal = true;
  }

  closeDeleteAppModal(): void {
    this.showDeleteAppModal = false;
    this.deletingApp = null;
    this.deleteAppStep = 'confirm';
  }

  onDeleteModalBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && this.deleteAppStep !== 'loading') {
      this.closeDeleteAppModal();
    }
  }

  confirmDeleteApp(): void {
    if (!this.deletingApp) return;
    this.deleteAppStep = 'loading';

    this.clinicAppService.deleteApp(this.deletingApp.id).subscribe({
      next: () => {
        this.store.dispatch(setWarningText('App removido com sucesso!'));
        this.closeDeleteAppModal();
        this.loadClinicApps();
      },
      error: (err) => {
        this.deleteAppStep = 'confirm';
        const msg = err?.error?.message || 'Erro ao remover app.';
        this.store.dispatch(setWarningText(msg));
      }
    });
  }

  getScopeLabel(scope: string): string {
    const found = AVAILABLE_SCOPES.find(s => s.value === scope);
    return found ? found.label : scope;
  }

  private loadClinicGroups(): void {
    if (!this.isRoot) return;
    this.isLoadingGroups = true;
    this.clinicGroupService.getGroups().subscribe({
      next: (groups: IClinicGroup[]) => {
        this.clinicGroupOptions = [
          { value: '', label: 'Nenhum (sem grupo)' },
          ...groups.map(g => ({ value: g.id || '', label: g.name }))
        ];
        this.isLoadingGroups = false;
      },
      error: () => {
        this.clinicGroupOptions = [{ value: '', label: 'Nenhum (sem grupo)' }];
        this.isLoadingGroups = false;
      }
    });
  }
}
